import React, { useState } from 'react';
import { Button, Input, Select, Upload, message, Form } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';

const { Option } = Select;
const { TextArea } = Input;

const AddNews = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [content, setContent] = useState('');
  const navigate = useNavigate();

  const handleImageChange = ({ fileList: newFileList }) => {
    const valid = newFileList.filter(f => f.originFileObj && f.originFileObj.type?.startsWith('image/'));
    setFileList(valid);
  };

  const beforeUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      message.error('يسمح فقط بملفات الصور');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error('حجم الصورة يجب ألا يتجاوز 5 ميجابايت');
      return Upload.LIST_IGNORE;
    }
    return false; // منع الرفع التلقائي
  };

  const handleSubmit = async (values) => {
    console.log('تشغيل handleSubmit', values, fileList);

    // ✅ المحتوى من المحرر
    const plainTextContent = content.replace(/<[^>]+>/g, '').trim();
    if (!plainTextContent && fileList.length === 0) {
      message.error('يرجى كتابة محتوى المنشور أو رفع صورة واحدة على الأقل');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('admin_token');

      // ✅ إرسال Multipart FormData إلى /api/news مع حقل images (صورة واحدة أو عدة صور)
      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('summary', values.summary || '');
      formData.append('content', content);
      formData.append('status', values.status || 'draft');
      fileList.forEach(file => {
        if (file.originFileObj) {
          formData.append('images', file.originFileObj);
        }
      });

      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/news`, {
        method: 'POST',
        headers: {
          // لا تقم بتحديد Content-Type يدوياً حتى يُضاف boundary تلقائياً
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      console.log('تم إرسال الطلب، status:', res.status);

      if (res.ok) {
        message.success('تم نشر المنشور بنجاح!');
        form.resetFields();
        setFileList([]);
        setContent('');
        navigate('/admin/news');
      } else {
        let errorMsg = 'لم نستطع نشر المنشور.';
        try {
          const data = await res.json();
          if (data && data.error) {
            errorMsg = data.error;
            console.log('خطأ من السيرفر:', data);
          } else if (data && data.message) {
            errorMsg = data.message;
            console.log('رسالة من السيرفر:', data);
          }
        } catch (e) {
          console.log('خطأ في قراءة رد السيرفر:', e);
        }

        if (errorMsg.includes('حجم الملف')) {
          message.error('حجم الصورة كبير جداً. الحد الأقصى 5 ميجابايت');
        } else if (errorMsg.includes('ملفات الصور')) {
          message.error('يسمح فقط بملفات الصور (jpg, png, webp)');
        } else if (errorMsg.includes('نوع الصورة')) {
          message.error('نوع الصورة غير مدعوم. يسمح فقط بـ jpg, png, webp');
        } else if (errorMsg.includes('عدد الملفات')) {
          message.error('عدد الصور كبير جداً. الحد الأقصى 10 صور');
        } else {
          message.error(errorMsg);
        }
      }
    } catch (err) {
      console.log('خطأ في fetch:', err);
      message.error('تعذر الاتصال بالخادم');
    } finally {
      setSubmitting(false);
      console.log('انتهت عملية النشر');
    }
  };

  return (
    <>
      <Helmet>
        <title>إضافة منشور | لوحة التحكم</title>
      </Helmet>
      <div style={{ maxWidth: 700, margin: '0 auto', background: '#fff', padding: 24, borderRadius: 12, boxShadow: '0 2px 8px #eee' }}>
        <h2 style={{ marginBottom: 24 }}>إضافة منشور جديد (رفع منشور)</h2>
        
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item label="عنوان المنشور" name="title" rules={[{ required: true, message: 'يرجى إدخال العنوان' }]}> 
            <Input placeholder="عنوان المنشور" />
          </Form.Item>
          
          <Form.Item label="ملخص المنشور" name="summary">
            <TextArea 
              placeholder="ملخص مختصر (اختياري عند رفع صور)"
              rows={3}
              maxLength={200}
              showCount
            />
          </Form.Item>
          
          <Form.Item label="محتوى المنشور" name="content" validateTrigger="onEditorChange">
            <Editor
             licenseKey="gpl"
              tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
              value={content}
              onEditorChange={(newContent) => {
                setContent(newContent);
                form.setFieldsValue({ content: newContent });
              }}
              init={{
                plugins: [
                  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
                  'searchreplace', 'visualblocks', 'fullscreen', 'insertdatetime', 'media', 'table',
                  'help', 'wordcount', 'code', 'emoticons', 'hr', 'pagebreak', 'nonbreaking', 'directionality'
                ],
                toolbar:
                  'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough forecolor backcolor | ' +
                  'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ' +
                  'removeformat | link image media table | code fullscreen preview | ltr rtl | emoticons',
                paste_data_images: false,
                images_dataimg_filter: () => false,
                language: 'ar',
                directionality: 'rtl',
                height: 300,
                content_style: 'body { font-family:Tahoma,Arial,sans-serif; font-size:16px }',
                paste_as_text: false,
                paste_enable_default_filters: true,
                paste_remove_styles_if_webkit: true,
                paste_retain_style_properties: 'color font-size font-family',
                images_upload_url: `${process.env.REACT_APP_API_URL || ''}/api/upload`,
                images_upload_handler: async (blobInfo, success, failure) => {
                  const formData = new FormData();
                  formData.append('file', blobInfo.blob());
                  try {
                    const token = localStorage.getItem('admin_token');
                    const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/upload`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                      body: formData
                    });
                    const data = await res.json();
                    if (data && data.url) {
                      let url = data.url.replace(/^\.\.\//, '/');
                      success(url);
                    } else {
                      failure('فشل رفع الصورة');
                    }
                  } catch (e) {
                    failure('فشل الاتصال بالخادم');
                  }
                },
                file_picker_callback: function (callback, value, meta) {
                  if (meta.filetype === 'image') {
                    const input = document.createElement('input');
                    input.setAttribute('type', 'file');
                    input.setAttribute('accept', 'image/*');
                    input.onchange = async function () {
                      const file = this.files[0];
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const token = localStorage.getItem('admin_token');
                        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/upload`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData
                        });
                        const data = await res.json();
                        if (data && data.url) {
                          let url = data.url.replace(/^\.\.\//, '/');
                          callback(url, { title: file.name });
                        } else {
                          alert('فشل رفع الصورة');
                        }
                      } catch (e) {
                        alert('فشل الاتصال بالخادم');
                      }
                    };
                    input.click();
                  }
                },
              }}
            />
          </Form.Item>
          
          <Form.Item label="حالة النشر" name="status" initialValue="draft"> 
            <Select placeholder="اختر حالة النشر">
              <Option value="draft">مسودة</Option>
              <Option value="published">منشور</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            label="صور المنشور"
            extra="يمكن رفع صورة واحدة أو عدة صور (حتى 10 صور). هذه الصور هي التي تظهر في صفحة أعمالنا."
          >
            <Upload
              listType="picture-card"
              multiple
              accept="image/*"
              fileList={fileList}
              beforeUpload={beforeUpload}
              onChange={handleImageChange}
              maxCount={10}
            >
              {fileList.length >= 10 ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>رفع صور</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting} style={{ marginInlineEnd: 8 }}>
              {submitting ? 'جاري النشر...' : 'نشر المنشور'}
            </Button>
            <Button onClick={() => navigate('/admin/news')}>
              إلغاء
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default AddNews;
